<template>
  <dx-modal :value="show" :title="title" size="lg" :closable="!saving"
            @input="close()" id="modal-appeal-response">
    <div class="billing-gloss appeal-response">
      <div class="loading loading-lg" v-if="loading"></div>
      <template v-else-if="appeal">
        <div class="bg-response-head">
          <span class="bg-pill bg-pill-outline">{{ roundName }}</span>
          <span class="bg-pill bg-pill-amber" v-if="appeal.dates && appeal.dates.sentAt">
            Enviado {{ appeal.dates.sentAt | date('DD/MM') }}
          </span>
          <span class="text-xs text-gray">
            · Lote {{ appeal.batch ? appeal.batch.number : appeal.batchNumber }} ·
            {{ insuranceName }}
          </span>
        </div>
        <div class="bg-note bg-note-info" v-if="prefill">
          Pré-preenchido pelo demonstrativo de pagamento (tipo Recurso, pago em
          {{ prefill.respondedAt | date }}):
          {{ prefillTotal | currency }} acatados. Confira item a item antes de registrar.
        </div>
        <div class="stenci-group stenci-alert bg-help" v-else>
          <p>
            Informe o que a operadora respondeu por item. O valor acatado gera uma receita
            complementar no
            título e ajusta o repasse.
          </p>
        </div>
        <div class="bg-note bg-note-amber" v-if="appeal.status !== 'sent'">
          Este recurso está {{ statusName.toLowerCase() }}: só um recurso enviado recebe resposta.
        </div>

        <div class="columns bg-response-form">
          <div class="column col-3 col-sm-12 form-group"
               :class="{'has-error': $v.form.respondedAt.$error}">
            <label class="form-label">Respondido em</label>
            <dx-input-date id="response-date" class="form-input" v-model="form.respondedAt"
                           @blur="$v.form.respondedAt.$touch()"/>
            <template v-if="$v.form.respondedAt.$error">
              <div class="form-input-hint" v-if="!$v.form.respondedAt.required">
                Campo obrigatório
              </div>
              <div class="form-input-hint" v-else-if="!$v.form.respondedAt.maxDate">
                Não pode ser futura
              </div>
              <div class="form-input-hint" v-else>Data inválida</div>
            </template>
          </div>
          <div class="column col-4 col-sm-12 form-group">
            <label class="form-label">
              Protocolo da resposta <span class="text-gray">(opcional)</span>
            </label>
            <input type="text" class="form-input" v-model="form.protocolNumber" maxlength="20">
          </div>
          <div class="column col-5 col-sm-12 form-group">
            <label class="form-label">Atalho</label>
            <div class="btn-group bg-seg">
              <button class="btn btn-sm btn-gray" @click="setAll('accepted')">Acatar tudo</button>
              <button class="btn btn-sm btn-gray" @click="setAll('maintained')">Manter tudo</button>
            </div>
          </div>
        </div>

        <div class="cr-panel cr-panel-first">
          <div class="cr-panel-head">
            <div class="cr-panel-title cr-panel-title-sm">
              {{ responses.length }}
              {{ responses.length === 1 ? 'item recursado' : 'itens recursados' }}
              <span class="text-gray bg-head-sub">· {{ totals.appealed | currency }}</span>
            </div>
          </div>
          <div class="bg-item-card" v-for="(response, index) in responses" :key="response.glossId"
               :class="cardClass(response)">
            <div class="bg-item-top">
              <span class="bg-seq" v-if="response.item.sequencialItem">
                {{ response.item.sequencialItem }}
              </span>
              <span class="bg-pill bg-pill-red" v-else>Guia inteira</span>
              <span class="bg-proc">
                <code>{{ response.item.code }}</code><b>{{ response.item.description }}</b>
              </span>
              <span class="text-xs text-gray">{{ response.guideLabel }}</span>
              <gloss-code-pill :code="response.item.glossCode"/>
              <span class="bg-pill bg-pill-outline" v-if="response.outcomeCode">
                {{ response.outcomeCode }}
              </span>
              <div class="bg-item-vals">
                <div>
                  <small>Recursado</small><span>{{ response.item.appealedValue | currency }}</span>
                </div>
              </div>
            </div>
            <div class="bg-response-fields">
              <div class="form-group" :class="{'has-error': $v.responses.$each[index].mode.$error}">
                <label class="form-label">Resposta</label>
                <div class="btn-group bg-seg">
                  <button class="btn btn-sm" v-for="option in modes" :key="option.value"
                          :class="response.mode === option.value ? 'btn-secondary' : 'btn-gray'"
                          @click="setMode(response, option.value)">{{ option.label }}</button>
                </div>
                <div class="form-input-hint" v-if="$v.responses.$each[index].mode.$error">
                  Escolha a resposta
                </div>
              </div>
              <div class="form-group"
                   :class="{'has-error': $v.responses.$each[index].acceptedValue.$error}">
                <label class="form-label">Valor acatado</label>
                <div class="input-group">
                  <span class="input-group-addon addon-sm">R$</span>
                  <dx-input-number :id="`accepted-${response.glossId}`"
                  v-model="response.acceptedValue"
                                   class="form-input text-right" :precision="2" maxlength="10"
                                   :disabled="response.mode !== 'partial'"
                                   @blur="$v.responses.$each[index].acceptedValue.$touch()"/>
                </div>
                <div class="form-input-hint" v-if="$v.responses.$each[index].acceptedValue.$error">
                  Entre 0,01 e {{ partialMax(response) | currency }}
                </div>
                <div class="text-xs text-gray" v-else-if="response.mode === 'partial'">
                  entre 0,01 e {{ partialMax(response) | currency }}
                </div>
              </div>
              <div class="form-group">
                <label class="form-label">
                  Justificativa da operadora <span class="text-gray">(opcional)</span>
                </label>
                <input type="text" class="form-input" v-model="response.operatorJustification"
                maxlength="1000"
                       placeholder="Ex.: 2902 · Glosa mantida">
              </div>
            </div>
            <div class="bg-item-effect" :class="effectClass(response)" v-if="response.mode">
              <fa-icon :icon="['fal', 'arrow-right']"/> {{ effectText(response) }}
            </div>
          </div>
          <div class="cr-panel-foot bg-response-foot">
            <span>Recursado <b>{{ totals.appealed | currency }}</b></span>
            <span>
              Acatado <b class="text-green">{{ totals.accepted | currency }}</b>
              <span class="text-xs text-gray">({{ totals.percent }}%)</span>
            </span>
            <span>Mantido <b class="text-red">{{ totals.maintained | currency }}</b></span>
            <span class="grow"></span>
            <span>
              Título <b class="text-green">+{{ totals.accepted | currency }}</b>
              · Repasses <b class="text-green">+{{ transferTotal | currency }}</b>
              <span class="text-xs text-amber" v-if="paidAdjustments > 0">
                ( {{ paidAdjustments }}
                {{ paidAdjustments === 1
                  ? 'crédito em repasse pago'
                  : 'créditos em repasses pagos' }})
              </span>
            </span>
          </div>
        </div>

        <div class="bg-note bg-note-amber mt-2" v-if="maintainedCount > 0 && allowSecondAppeal">
          {{ maintainedCount }} {{ maintainedCount === 1 ? 'item mantido' : 'itens mantidos' }}
          ( {{ totals.maintained | currency }}) {{ maintainedCount === 1 ? 'pode' : 'podem' }}
          ir para a
          <b>
            2ª instância</b>: o convênio permite. Após registrar, selecione na aba Glosas e clique
            em Recursar.
        </div>
        <div class="bg-note bg-note-red mt-2" v-else-if="maintainedCount > 0">
          {{ maintainedCount }} {{ maintainedCount === 1 ? 'item mantido' : 'itens mantidos' }}
          (
          {{ totals.maintained | currency }}): o convênio não aceita 2ª instância, o valor vira
          perda.
        </div>
      </template>
    </div>

    <template slot="footer">
      <button class="btn btn-primary btn-icon btn-icon-left mr-1 tooltip tooltip-right"
              :data-tooltip="missing > 0 ? `${missing} ${missing === 1 ? 'item sem resposta' :
              'itens sem resposta'}` : null"
              :class="{ loading: saving }"
              :disabled="saving || loading || missing > 0 || !appeal || appeal.status !== 'sent' ||
              !canManage"
              @click="save">
        <fa-icon :icon="['fal', 'check']"/>Registrar resposta
      </button>
      <span class="text-xs text-gray mr-1" v-if="appeal && appeal.status === 'sent'">
        Lote volta a <b>Analisado</b>
        <template v-if="totals.accepted > 0">
          · receita RECUPERACAO LOTE {{ appeal.batchNumber }}/R{{ appeal.round }} criada
        </template>
      </span>
      <button class="btn" :disabled="saving" @click="close()">Sair</button>
    </template>
  </dx-modal>
</template>

<script>
import moment from 'moment';
import { required, requiredIf } from 'vuelidate/lib/validators';
import { date, maxDate } from '@/data/validators';
import { MANAGE_BILLING_GLOSSES } from '@/data/actions/modules/financial';
import { getName as appealStatusName, getRoundName } from '@/data/billing-appeal-statuses';
import GlossCodePill from '../components/GlossCodePill.vue';
import {
  idOf, num, recoveryEffect, round2,
} from '../helpers';

const MODES = [
  { value: 'accepted', label: 'Acatado' },
  { value: 'partial', label: 'Parcial' },
  { value: 'maintained', label: 'Mantido' },
];

export default {
  components: {
    GlossCodePill,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    appealId: {
      type: String,
      required: true,
    },
    prefill: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      modes: MODES,
      loading: true,
      saving: false,
      appeal: null,
      view: null,
      financial: null,
      form: {
        respondedAt: moment().format('YYYY-MM-DD'),
        protocolNumber: '',
      },
      responses: [],
    };
  },
  computed: {
    canManage() {
      return this.$can(MANAGE_BILLING_GLOSSES);
    },
    title() {
      return this.appeal ? `Resposta do recurso ${this.appeal.number}` : 'Resposta do recurso';
    },
    roundName() {
      return getRoundName(this.appeal ? this.appeal.round : 1);
    },
    statusName() {
      return appealStatusName(this.appeal ? this.appeal.status : 'sent');
    },
    insuranceName() {
      const insurance = this.appeal && this.appeal.insurance;
      if (!insurance) return '';
      const plan = insurance.plan && insurance.plan.name;
      return plan && plan !== insurance.name ? `${insurance.name} · ${plan}` : insurance.name;
    },
    allowSecondAppeal() {
      return !!(this.appeal && this.appeal.allowSecondAppeal);
    },
    prefillTotal() {
      const items = (this.prefill && this.prefill.items) || [];
      return round2(items.reduce((sum, item) => sum + num(item.acceptedValue), 0));
    },
    itemMap() {
      const map = new Map();
      if (!this.view) return map;
      this.view.guides.forEach(guide => guide.items.forEach((item) => {
        map.set(String(item.itemId), { guide, item });
      }));
      return map;
    },
    totals() {
      const appealed = round2(this.responses
        .reduce((sum, response) => sum + num(response.item.appealedValue), 0));
      const accepted = round2(this.responses
        .reduce((sum, response) => sum + this.acceptedOf(response), 0));
      return {
        appealed,
        accepted,
        maintained: round2(appealed - accepted),
        percent: appealed > 0 ? Math.round((accepted / appealed) * 100) : 0,
      };
    },
    transferLines() {
      return this.responses.map(response => this.transferOf(response)).filter(Boolean);
    },
    transferTotal() {
      return round2(this.transferLines.reduce((sum, line) => sum + line.credit, 0));
    },
    paidAdjustments() {
      return new Set(this.transferLines.filter(line => line.paid).map(line => line.key)).size;
    },
    maintainedCount() {
      return this.responses
        .filter(response => response.mode
          && this.acceptedOf(response) < num(response.item.appealedValue))
        .length;
    },
    missing() {
      return this.responses.filter(response => !response.mode).length;
    },
  },
  validations() {
    return {
      form: {
        respondedAt: { required, date, maxDate: maxDate(moment().format('YYYY-MM-DD')) },
      },
      responses: {
        $each: {
          mode: { required },
          acceptedValue: {
            required: requiredIf(response => response.mode === 'partial'),
            range: (value, response) => response.mode !== 'partial'
              || (num(value) >= 0.01
                && num(value) <= num(response.item.appealedValue) - 0.01 + 0.004),
          },
        },
      },
    };
  },
  mounted() {
    this.load();
  },
  methods: {
    load() {
      this.loading = true;
      return this.$http.get(`/billing-appeals/${this.appealId}`)
        .then(({ data }) => {
          this.appeal = data;
          return Promise.all([
            this.$http.get('/billing-glosses', { params: { batchId: String(data.batchId), onlyGlossed: false } })
              .catch(() => ({ data: null })),
            this.$http.get(`/billing-batches/${data.batchId}/financial`).catch(() => ({ data: null })),
          ]);
        })
        .then(([view, financial]) => {
          this.view = view.data;
          this.financial = financial.data;
          this.buildResponses();
        })
        .catch((e) => {
          this.$toast.error(e);
          this.close();
        })
        .finally(() => {
          this.loading = false;
        });
    },
    buildResponses() {
      const prefillItems = new Map(((this.prefill && this.prefill.items) || [])
        .map(item => [String(item.glossId), item]));
      if (this.prefill && this.prefill.respondedAt) {
        this.form.respondedAt = moment(this.prefill.respondedAt).format('YYYY-MM-DD');
      }
      this.responses = (this.appeal.guides || []).flatMap(guide => guide.items
        .filter(item => !(item.response && item.response.respondedAt))
        .map((item) => {
          const account = guide.account || {};
          const response = {
            glossId: String(item.glossId),
            item: {
              ...item,
              appealedValue: num(item.appealedValue),
            },
            guideLabel: [
              `Guia ${guide.numeroGuiaOrigem || ''}`.trim(),
              account.patient && account.patient.name,
            ].filter(Boolean).join(' · '),
            mode: null,
            acceptedValue: 0,
            operatorJustification: '',
            outcomeCode: null,
          };
          const prefilled = prefillItems.get(response.glossId);
          if (prefilled) {
            const accepted = round2(num(prefilled.acceptedValue));
            if (accepted <= 0) this.setMode(response, 'maintained');
            else if (accepted >= num(item.appealedValue)) this.setMode(response, 'accepted');
            else {
              this.setMode(response, 'partial');
              response.acceptedValue = accepted;
            }
          }
          return response;
        }));
    },
    setMode(response, mode) {
      response.mode = mode;
      if (mode === 'accepted') response.acceptedValue = num(response.item.appealedValue);
      else if (mode === 'maintained') response.acceptedValue = 0;
      else if (response.acceptedValue <= 0
        || response.acceptedValue >= num(response.item.appealedValue)) {
        response.acceptedValue = 0;
      }
    },
    setAll(mode) {
      this.responses.forEach(response => this.setMode(response, mode));
    },
    partialMax(response) {
      return round2(Math.max(num(response.item.appealedValue) - 0.01, 0.01));
    },
    acceptedOf(response) {
      if (!response.mode) return 0;
      if (response.mode === 'accepted') return num(response.item.appealedValue);
      if (response.mode === 'maintained') return 0;
      const value = Math.max(num(response.acceptedValue), 0);
      return round2(Math.min(value, num(response.item.appealedValue)));
    },
    transferOf(response) {
      const accepted = this.acceptedOf(response);
      if (accepted <= 0 || !response.item.itemId) return null;
      const entry = this.itemMap.get(String(response.item.itemId));
      if (!entry || !entry.item.transfer || !num(entry.item.transfer.amount)) return null;
      const credit = recoveryEffect({
        amount: entry.item.transfer.amount,
        insurance: entry.item.values.informed,
        value: accepted,
      });
      if (credit <= 0) return null;
      const transfers = (this.financial && this.financial.transfers) || [];
      const bundleId = entry.item.transfer.financialBundleId
        ? String(entry.item.transfer.financialBundleId)
        : null;
      const financial = bundleId
        ? transfers.find(doc => String(doc.bundleId) === bundleId) || null
        : null;
      return {
        key: bundleId || `professional:${idOf(entry.item.professional)}`,
        name: (financial && financial.entity && financial.entity.name)
          || (entry.item.professional && entry.item.professional.name) || 'Profissional',
        paid: financial ? financial.status === 'paid' : false,
        credit,
      };
    },
    cardClass(response) {
      if (!response.mode) return null;
      if (response.mode === 'accepted') return 'bg-item-card-ok';
      if (response.mode === 'partial') return 'bg-item-card-partial';
      return 'bg-item-card-maintained';
    },
    effectClass(response) {
      return response.mode === 'maintained' ? 'text-red' : 'text-green';
    },
    effectText(response) {
      const accepted = this.acceptedOf(response);
      const transfer = this.transferOf(response);
      const { currency } = this.$options.filters;
      if (response.mode === 'maintained') {
        return `Glosa fica Mantida · sem efeito no título${this.allowSecondAppeal ? ' · pode ir para a 2ª instância' : ' · vira perda'}`;
      }
      const parts = [
        `Glosa fica ${response.mode === 'accepted' ? 'Acatada' : 'Acatada parcial'}`,
        `título +${currency(accepted)}`,
      ];
      if (transfer) {
        parts.push(`repasse ${transfer.name} +${currency(transfer.credit)}${transfer.paid ? ' (já pago: crédito no próximo)' : ''}`);
      }
      if (response.mode === 'partial') {
        const rest = round2(num(response.item.appealedValue) - accepted);
        parts.push(`${currency(rest)} mantidos${this.allowSecondAppeal ? ' podem ir à 2ª instância' : ''}`);
      }
      return parts.join(' · ');
    },
    save() {
      this.$v.$touch();
      if (this.$v.$error || this.missing > 0) return Promise.resolve();

      this.saving = true;
      const payload = {
        respondedAt: this.form.respondedAt,
        protocolNumber: this.form.protocolNumber.trim() || null,
        items: this.responses.map(response => ({
          glossId: response.glossId,
          accepted: response.mode !== 'maintained',
          acceptedValue: this.acceptedOf(response),
          operatorJustification: response.operatorJustification.trim() || null,
        })),
      };
      return this.$http.post(`/billing-appeals/${this.appealId}/response`, payload)
        .then(({ data }) => {
          this.$toast.success(`Resposta registrada: ${this.$options.filters.currency(data.recovered)} recuperados.`);
          this.$emit('saved', data);
          this.close(true);
        })
        .catch((e) => {
          this.$toast.error(e);
        })
        .finally(() => {
          this.saving = false;
        });
    },
    close(saved = false) {
      this.$emit('close', saved);
    },
  },
};
</script>

<style lang="scss">
@import "../gloss.scss";

#modal-appeal-response {
  .appeal-response {
    .bg-response-head {
      align-items: center;
      display: flex;
      flex-wrap: wrap;
      gap: $layout-spacing-sm;
      margin-bottom: $layout-spacing;
    }
    .bg-response-form { margin-bottom: 0; }
    .bg-head-sub { font-weight: 400; }
    .bg-response-fields {
      align-items: end;
      display: grid;
      gap: $layout-spacing $layout-spacing-lg;
      grid-template-columns: auto 8rem minmax(0, 1fr);
      margin-top: $layout-spacing;
      @media screen and (max-width: $size-sm) { grid-template-columns: 1fr; }
    }
    .bg-response-foot { justify-content: flex-start; }
  }
}
</style>
